import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Building2, Ticket,
  Euro, RefreshCcw, Plus, ChevronRight,
  Clock, CheckCircle, UserCircle, BarChart3
} from 'lucide-react';

const SuperAdminDashboard = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);

  const fetchDashboard = useCallback(async ({ manual = false } = {}) => {
    if (manual) setRefreshing(true);
    try {
      const response = await api.get('/super-admin/dashboard', {
        params: { _ts: Date.now() },
        headers: { 'Cache-Control': 'no-cache' },
      });
      setData(response.data);
      if (manual) toast.success('Tableau de bord actualisé');
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Erreur lors du chargement du dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCcw className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  const stats = data?.statistics || {};
  const revenue = data?.revenue || {};

  const mainStats = [
    { 
      label: 'Centres', 
      value: stats.total_centers, 
      icon: Building2, 
      color: 'bg-violet-500',
      path: '/admin/centers',
      detail: `${stats.active_centers} actifs • ${stats.trial_centers} essai`
    },
    { 
      label: 'Admin CFA', 
      value: stats.total_cfa_admins, 
      icon: UserCircle, 
      color: 'bg-orange-500',
      path: '/admin/cfa-admins',
      detail: 'Comptes actifs'
    },
  ];

  const ticketStats = [
    { 
      label: 'Tickets ouverts', 
      value: stats.open_tickets, 
      icon: Ticket, 
      color: 'text-orange-500',
      path: '/admin/platform-support'
    },
    { 
      label: 'En attente réponse', 
      value: stats.waiting_response, 
      icon: Clock, 
      color: 'text-yellow-500',
      path: '/admin/platform-support?status=waiting_response'
    },
  ];

  return (
    <div className="space-y-6 p-6" data-testid="super-admin-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <button onClick={onMenuClick} className="md:hidden p-2 -ml-2 rounded-lg text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800" aria-label="Ouvrir le menu">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              Cockpit Super Admin
            </h1>
            <p className="text-slate-500 dark:text-slate-400">
              Vue globale de la plateforme OFPilot
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            disabled={refreshing}
            onClick={() => fetchDashboard({ manual: true })}
          >
            <RefreshCcw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Actualisation…' : 'Actualiser'}
          </Button>
          <Button type="button" onClick={() => navigate('/admin/centers')} className="bg-violet-600 hover:bg-violet-700">
            <Plus className="w-4 h-4 mr-2" />
            Nouveau Centre
          </Button>
        </div>
      </div>

      {/* Revenue Card */}
      <Card className="bg-gradient-to-r from-violet-600 to-purple-600 text-white">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-violet-100 text-sm">Revenus mensuels attendus</p>
              <p className="text-4xl font-bold">{revenue.monthly_expected?.toLocaleString()}€</p>
            </div>
            <div className="flex gap-6">
              {Object.entries(revenue.plans_breakdown || {}).map(([plan, count]) => (
                <div key={plan} className="text-center">
                  <p className="text-3xl font-bold">{count}</p>
                  <p className="text-violet-200 text-sm">{plan}</p>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-2 gap-4">
        {mainStats.map((stat, idx) => (
          <Card 
            key={idx}
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate(stat.path)}
            data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className={`p-2 rounded-lg ${stat.color}`}>
                  <stat.icon className="w-5 h-5 text-white" />
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </div>
              <p className="mt-3 text-2xl font-bold text-slate-900 dark:text-white">
                {stat.value?.toLocaleString() || 0}
              </p>
              <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                {stat.label}
              </p>
              <p className="text-xs text-slate-400 mt-1">{stat.detail}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tickets & Alerts Row */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Tickets */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Ticket className="w-5 h-5 text-orange-500" />
              Support Plateforme
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 mb-4">
              {ticketStats.map((stat, idx) => (
                <div 
                  key={idx}
                  className="flex-1 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700"
                  onClick={() => navigate(stat.path)}
                >
                  <div className="flex items-center gap-2">
                    <stat.icon className={`w-5 h-5 ${stat.color}`} />
                    <span className="text-2xl font-bold text-slate-900 dark:text-white">
                      {stat.value}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
            
            {/* Recent Tickets */}
            <div className="space-y-2">
              {data?.recent_tickets?.length > 0 ? (
                data.recent_tickets.map(ticket => (
                  <div 
                    key={ticket.id}
                    className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg cursor-pointer hover:bg-slate-100"
                    onClick={() => navigate(`/admin/platform-support/${ticket.id}`)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 dark:text-white truncate">
                        {ticket.subject}
                      </p>
                      <p className="text-sm text-slate-500">{ticket.organization_name}</p>
                    </div>
                    <Badge variant={
                      ticket.priority === 'urgent' ? 'destructive' :
                      ticket.priority === 'high' ? 'warning' : 'secondary'
                    }>
                      {ticket.priority}
                    </Badge>
                  </div>
                ))
              ) : (
                <div className="text-center py-6 text-slate-500">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                  <p>Aucun ticket en attente</p>
                </div>
              )}
            </div>
            
            <Button 
              variant="outline" 
              className="w-full mt-4"
              onClick={() => navigate('/admin/platform-support')}
            >
              Voir tous les tickets
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>

        {/* Recent Centers */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building2 className="w-5 h-5 text-violet-500" />
              Centres récents
            </CardTitle>
            <CardDescription>7 derniers jours</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data?.recent_centers?.length > 0 ? (
                data.recent_centers.map(center => (
                  <div 
                    key={center.id}
                    className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg cursor-pointer hover:bg-slate-100"
                    onClick={() => navigate(`/admin/centers/${center.id}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-900 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-violet-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">
                          {center.name}
                        </p>
                        <p className="text-sm text-slate-500">{center.city || 'Ville non renseignée'}</p>
                      </div>
                    </div>
                    <Badge variant={center.status === 'active' ? 'default' : 'secondary'}>
                      {center.status === 'active' ? 'Actif' : 
                       center.status === 'trial' ? 'Essai' : center.status}
                    </Badge>
                  </div>
                ))
              ) : (
                <div className="text-center py-6 text-slate-500">
                  <Building2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Aucun nouveau centre</p>
                </div>
              )}
            </div>
            
            <Button 
              variant="outline" 
              className="w-full mt-4"
              onClick={() => navigate('/admin/centers')}
            >
              Voir tous les centres
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Actions rapides</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2"
              onClick={() => navigate('/admin/centers?action=create')}
            >
              <Plus className="w-5 h-5 text-violet-600" />
              <span>Créer un centre</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2"
              onClick={() => navigate('/admin/cfa-admins')}
            >
              <UserCircle className="w-5 h-5 text-blue-600" />
              <span>Gérer les admins</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2"
              onClick={() => navigate('/admin/analytics')}
            >
              <BarChart3 className="w-5 h-5 text-emerald-600" />
              <span>Analytics</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2"
              onClick={() => navigate('/admin/subscriptions')}
            >
              <Euro className="w-5 h-5 text-orange-600" />
              <span>Abonnements</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperAdminDashboard;
